import {
  DEFAULT_FOLDER_KEY,
  IS_FOLDERS_CACHE_ENABLE,
  MOCK_FOLDERS_URL,
} from "@/constants";
import { IPanelScope } from "@/interfaces/IPanelScope";
import { isStoryBookDomain } from "@/utils";
import { IFolderServerResponse, ITreeItem } from "@/interfaces";
import { HttpService } from "./http.service";
import { CacheService, SearchService, TreeService } from ".";
import { LocalStorageKeys } from "@/enums";
import { findNodeById, mergeUrlParams, parseUrl } from "@/utils/common";

const foldersService = () => {
  const getFolderChildren = async (
    scope: IPanelScope,
    id: string,
  ): Promise<ITreeItem[]> => {
    const url = `/p/${scope.projectId}/folders/${id}?entity_name=${scope.module}`;
    const data = await HttpService.get(url);
    const childrens = transformServerToClient(data.node.children);
    return childrens;
  };

  const fetchFolder = async (
    scope: IPanelScope,
    id: string,
  ): Promise<ITreeItem | undefined> => {
    const url = `/p/${scope.projectId}/folders/${id}?entity_name=${scope.module}`;
    const data = await HttpService.get(url);
    const filters = transformServerToClient([data.node]);
    return filters?.[0];
  };

  const fetchFoldersFromServer = async (
    scope: IPanelScope,
  ): Promise<{ items: ITreeItem[] }> => {
    const cachedObj = IS_FOLDERS_CACHE_ENABLE
      ? await CacheService.getItemFromPersistCache(
          scope,
          LocalStorageKeys.FOLDERS_ITEMS_KEY,
        )
      : undefined;

    const { projectId, module } = scope;
    const viewsCount = cachedObj?.viewsCount || -1;
    const cachedFolders = cachedObj?.items;
    const listOfNodeShouldBeFetch = TreeService.getOpenedNodes(scope);
    const activeNode = TreeService.getActiveNode(scope);
    const activeNodeAncestries =
      activeNode?.serverObject?.ancestry?.split("/") || [];

    listOfNodeShouldBeFetch.push(...activeNodeAncestries);

    const selectedItem = FoldersService.getActiveFolderFromUrl();

    selectedItem && listOfNodeShouldBeFetch.push(selectedItem);
    const uniqueList = [...new Set(listOfNodeShouldBeFetch)];

    const openIds = uniqueList.join(",");
    TreeService.setOpenedNodes(scope, uniqueList);

    const fetchFoldersApi = isStoryBookDomain
      ? MOCK_FOLDERS_URL
      : `/p/${projectId}/folders?entity_name=${module}&ids=${openIds}&views_count=${viewsCount}}`;

    const data = await HttpService.get(fetchFoldersApi);

    if (!!cachedFolders && !data.items) {
      return cachedFolders!;
    }

    const serverFolders = data?.items || data;

    const items: ITreeItem[] = transformServerToClient(serverFolders);

    try {
      CacheService.removeItemFromPersistCache(
        scope,
        LocalStorageKeys.FOLDERS_ITEMS_KEY,
      );
    } catch (e) {
      console.error(
        `fetchFoldersFromServer-> failed to save filter to localstorage `,
        e,
      );
    }
    setActiveFolderFromUrl(scope, items);

    return { items };
  };

  const setActiveFolderFromUrl = (scope: IPanelScope, items: ITreeItem[]) => {
    const params = new URLSearchParams(window.location.search);
    const folder = params.get("folder");
    if (folder) {
      if (folder === DEFAULT_FOLDER_KEY) {
        TreeService.setActiveNode(scope, null);
      } else {
        const item = SearchService.findFilterItem(items, folder);
        item && TreeService.setActiveNode(scope, item);
      }
    }
  };

  const getActiveFolderFromUrl = (): string | null => {
    const searchParams = new URLSearchParams(window.location.search);
    //old ui support
    return searchParams.get("folder");
  };

  const fetchFoldersFromCache = ({
    module,
    projectId,
    tab,
  }: IPanelScope): IFolderServerResponse | null => {
    const cachedFolders = localStorage.getItem(
      `${tab}-${module}-${projectId}`.toLowerCase(),
    );
    return cachedFolders ? JSON.parse(cachedFolders) : null;
  };

  const prepareServerObject = (node: any): any => {
    //const { ...serverWithoutChildren } = node;
    return node;
  };

  const transformServerToClient = (node: any): ITreeItem[] => {
    const filters: ITreeItem[] = []; // Initialize activeFolders array

    node.forEach((item: any) => {
      const folder: ITreeItem = {
        serverObject: prepareServerObject(item),
        key: item.id,
        name: item.name,
        id: item.id + "",
        children: [],
        extra: {
          paddingLeft: TreeService.getPaddingLeft(item),
          hasChildren: !!item?.children?.length || item?.has_children,
        },
      };
      if (item.children) {
        folder.children = transformServerToClient(item.children);
      }
      filters.push(folder);
    });
    return filters;
  };

  const deleteFolder = async (
    scope: IPanelScope,
    id: string,
  ): Promise<void> => {
    await HttpService.delete(`/p/${scope.projectId}/folders/${id}`);
  };

  const addFirstLevelFolder = async (
    { projectId, module }: IPanelScope,
    folderName: string,
  ): Promise<any> => {
    const formData = new FormData();
    formData.append("folder[name]", folderName);

    return await HttpService.post(
      `/p/${projectId}/folders?entity_name=${module}`,
      formData,
    );
  };

  const getModuleDisplayName = (module: string): string => {
    switch (module.toLowerCase()) {
      case "testset":
        return "Testsets";
      case "requirement":
        return "Requirements";
      case "issue":
        return "Issues";
      case "test":
        return "Tests";
      default:
        return "";
    }
  };

  const pushToHistory = (
    node: { id: string },
    isInitialLoad: boolean = false,
  ): string => {
    let searchParams = "";

    if (node?.id && node?.id !== DEFAULT_FOLDER_KEY) {
      searchParams = `folder=${node.id}`;
    } else {
      searchParams = `folder=${DEFAULT_FOLDER_KEY}`;
    }

    let newUrl = window.location.pathname;
    const hash = window.location.hash.split("?")[0];
    if (searchParams) {
      newUrl = `${newUrl}${hash}?${searchParams.toString()}`;
    }

    const currentUrl = window.location.href;
    const mergedUrl = mergeUrlParams(currentUrl, `${window.origin}${newUrl}`);

    // Save the current state before replacing it
    const currentState = history.state || {};
    currentState.previousParams = parseUrl(currentUrl).params.toString();

    // Update the URL without reloading the page
    isInitialLoad
      ? window.history.pushState(currentState, "", mergedUrl)
      : window.history.replaceState(currentState, "", mergedUrl);

    return newUrl;
  };

  const rename = async (
    projectId: string,
    folderId: string,
    value: string,
    items: ITreeItem[],
  ) => {
    await HttpService.put(
      `/p/${projectId}/folders/${folderId}/update_folder_name`,
      {
        folder_name: value,
      },
    );

    const treeNode = findNodeById(items, folderId);
    if (treeNode) {
      treeNode.name = value;
      treeNode.serverObject.name = value;
    }
  };

  return {
    fetchFoldersFromCache,
    fetchFoldersFromServer,
    addFirstLevelFolder,
    deleteFolder,
    getModuleDisplayName,
    getActiveFolderFromUrl,
    pushToHistory,
    rename,
    getFolderChildren,
    transformServerToClient,
    fetchFolder,
  };
};

export const FoldersService = foldersService();
